import type { Unsubscribe } from '@reduxjs/toolkit';
import h from 'hyperscript';

import dataBrowserIcon from '@disy/cadenza-icons/database.svg';
import errorIcon from '@disy/cadenza-icons/error.svg';
import { on } from '@disy/lib/event-util';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { entries, keys } from '@disy/lib/type-util';
import { createAnchorButton, createIconButton } from '@disy/ui/button';
import { icon } from '@disy/ui/icon';
import { POPUP_PARENT_MARKER_CLASS, setupPopupButton } from '@disy/ui/popup';

import { createDataSourceTile } from 'ui/datasource-tile/datasource-tile';
import { createResourceLabel } from 'ui/resource-label/resource-label';
import { Select } from 'ui/select/select';
import type { SelectItem, SelectOption, SelectOptionGroup } from 'ui/select/select-types';

import type {
  DatasourceSelectItem,
  DatasourceSelectOption,
  DatasourceSelectOptionGroup,
} from 'cadenza/admin/datasource-filter/datasource-filter';
import {
  templateDatasourceResult,
  templateGroupDatasourceResult,
} from 'cadenza/admin/datasource-filter/datasource-filter';
import { isComponentProjectObjecttype } from 'cadenza/project/is-component-project';
import { DataSourceId } from 'cadenza/repository/model/data-source-id.gen';
import type { DataSourceName } from 'cadenza/repository/model/data-source-name.gen';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ProjectId, ProjectIdString } from 'cadenza/repository/model/project-id.gen';
import { itemIdToString } from 'cadenza/repository/model/repository-item-id';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RootState } from 'cadenza/store/cadenza-store';
import { getStore } from 'cadenza/store/cadenza-store';
import { STATUS, observeStore, unsubscribeStore } from 'cadenza/store/state-utils';
import { type Objecttype, isObjecttypeBoundToProject } from 'cadenza/workbook/api/objecttype';
import { getBaseAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import {
  getActiveView,
  getAttributes,
  getDataSourceByObjecttypeId,
  getDataViewById,
  getInitializationStatus,
  getObjecttypeByAttributeId,
  getObjecttypeById,
  getObjecttypeDataDependencies,
  getProject,
  getProjectById,
  getUnavailableObjecttypeIds,
  getVisibleDataSources,
  getVisibleObjecttypes,
  getWorkbookNameAndHash,
  isWorkbookVisible,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { DataBrowserController } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-controller';
import { DataBrowserItemList } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-item-list';
import { WorkbookPanel } from 'cadenza/workbook/workbook-panel/workbook-panel';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import './data-browser-panel.css';
import i18n from './data-browser-panel.properties';

const COMPONENT_NAME = WorkbookPanelName.DATA_BROWSER;
const TITLE_ID = `${COMPONENT_NAME}-title`;

const SELECTED_SOURCE_STORAGE_KEY = 'WorkbookDataBrowserSelectedSource';

type WorkbookStorageSelectOptionId = '{WORKBOOK_STORAGE}';
type SourceSelectOptionId = DataSourceName | WorkbookStorageSelectOptionId | ProjectIdString;
type SelectItemGroupCategory = 'WORKBOOK_STORAGE' | 'COMPONENT_PROJECT' | 'DATASOURCE';

const SelectItemGroupCategoryOrder = [
  'WORKBOOK_STORAGE',
  'COMPONENT_PROJECT',
  'DATASOURCE',
] as SelectItemGroupCategory[];

interface WorkbookStorageSelectOption extends SelectOption {
  id: WorkbookStorageSelectOptionId;
}

interface ProjectSelectOption extends SelectOption {
  projectId: ProjectId;
}

interface ProjectSelectionGroup extends SelectOptionGroup {
  children: ProjectSelectOption[];
}

type SourceSelectItem = DatasourceSelectOptionGroup | WorkbookStorageSelectOption | ProjectSelectionGroup;

interface SourceSelectData {
  objecttypesBySelectOptionId: Partial<Record<SourceSelectOptionId, Objecttype[]>>;
  selectItems: SourceSelectItem[];
}

export class DataBrowserPanel extends WorkbookPanel {
  readonly #controller: DataBrowserController;
  #unsubscribeFromStore: Unsubscribe[] = [];
  #dataBrowserItemList!: DataBrowserItemList; // Initialized indirectly in constructor
  #sourceSelect!: Select; // Initialized indirectly in constructor
  #errorButton?: HTMLElement;

  #objectTypeIdsOfPreviousUpdate = new Set<ObjecttypeIdString>();
  #selectData?: SourceSelectData;

  constructor() {
    super(icon(dataBrowserIcon), i18n('header.title'), TITLE_ID); // no icon, because it would double the one in the datasource select
    this.#controller = new DataBrowserController();
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'complementary');
    this.setAttribute('aria-labelledby', TITLE_ID);
    this.dataset.testid = 'data-browser-panel';
    this.#createContent();

    this.#unsubscribeFromStore.push(
      observeStore(
        getStore(),
        getPanelData,
        () => {
          // Ignore state updates until workbook state is initialized
          if (getInitializationStatus(getStore().getState()) === STATUS.SUCCEEDED) {
            this.#updateSourceSelect();
          }
        },
        true,
      ),
    );
  }

  disconnectedCallback() {
    this.#unsubscribeFromStore = unsubscribeStore(this.#unsubscribeFromStore);
  }

  #createContent() {
    this.#sourceSelect = this.#createSourceSelect();
    this.#dataBrowserItemList = new DataBrowserItemList({
      actionsForAttributeIdProvider: (attributeId) => this.#controller.getMenuItems(attributeId as AttributeId),
    });
    this.setContent(this.#dataBrowserItemList);
  }

  #createSourceSelect() {
    const sourceSelect = new Select({
      templateSelection: renderSelection,
      templateResult: renderResult,
      templateGroupResult: (item) => {
        const groupItem = item as DatasourceSelectOptionGroup | ProjectSelectionGroup;
        const selectOption = groupItem.children[0];
        if (isProjectSelectOption(selectOption)) {
          return templateGroupProjectResult(groupItem as ProjectSelectionGroup);
        } else {
          return templateGroupDatasourceResult(item as DatasourceSelectItem);
        }
      },
      styleClass: [POPUP_PARENT_MARKER_CLASS],
    });
    on(sourceSelect, 'change', () => {
      // eslint-disable-next-line no-self-assign
      this.selectedSource = this.selectedSource;
    });
    return sourceSelect;
  }

  #updateSourceSelect() {
    // Remove datasource element (either select or read-only block)
    this.querySelector('.d-data-browser-panel--sources')?.remove();

    const objecttypes = getFilteredObjecttypes(getVisibleObjecttypes(getStore().getState()));
    this.#selectData = createSelectData(objecttypes);

    const isEmpty = this.#selectData.selectItems.length === 0;

    // Add new, updated datasource element
    if (!isEmpty) {
      this.#sourceSelect.setData(this.#selectData.selectItems);

      // Also triggers object type list update, even if selected source is undefined
      this.selectedSource = this.#determineSourceToSelect(objecttypes, this.#selectData);

      this.#objectTypeIdsOfPreviousUpdate = new Set(objecttypes.map(({ id }) => id));

      const selection = this.#sourceSelect.selection[0] as DatasourceSelectOption | WorkbookStorageSelectOption;

      const readOnly = Object.keys(this.#selectData.objecttypesBySelectOptionId).length === 1;
      this.header.after(
        h('.d-data-browser-panel--sources.light-bg', readOnly ? renderSelection(selection) : this.#sourceSelect),
      );
    } else {
      this.#sourceSelect.setData([]);
      this.#dataBrowserItemList.updateList([], isWorkbookVisible(getStore().getState()));
    }
  }

  override createHeaderElement(): HTMLElement {
    const header = super.createHeaderElement();
    observeStore(getStore(), getUnavailableObjecttypeIds, (unreachableObjecttypeIds) => {
      if (!unreachableObjecttypeIds.length) {
        this.#errorButton?.remove();
        this.#errorButton = undefined;
        return;
      }
      const newErrorButton = this.#createRemoveUnreachableObjecttypesButton();
      if (this.#errorButton) {
        this.#errorButton.replaceWith(newErrorButton);
      } else {
        header.insertBefore(newErrorButton, this._closeButton);
      }
      this.#errorButton = newErrorButton;
    });
    return header;
  }

  #createRemoveUnreachableObjecttypesButton() {
    const button = createIconButton(icon(errorIcon), i18n('error.title'), {
      variant: 'borderless',
      size: 's',
      styleClass: 'd-icon-error',
    });
    const count = getUnavailableObjecttypeIds(getStore().getState()).length;
    setupPopupButton(button, () => {
      return h('d-alert.d-alert-error.d-data-browser-panel--error-popup-content', [
        h('p', i18n('error.removeUnavailableObjecttypes.message', { count })),
        createAnchorButton(i18n('error.removeUnavailableObjecttypes.action'), {
          onclick: async () => {
            const { UnavailableObjecttypesDialog } = await import('./unavailable-objecttypes-dialog');
            new UnavailableObjecttypesDialog().connectAndShow();
          },
        }),
      ]);
    });
    return button;
  }

  #determineSourceToSelect(objecttypes: Objecttype[], selectData: SourceSelectData): SourceSelectOptionId {
    // 1. If new object types added, select first new objecttype's source
    if (this.#objectTypeIdsOfPreviousUpdate) {
      // Not on the initial display
      const firstNewObjecttype = objecttypes.find(({ id }) => !this.#objectTypeIdsOfPreviousUpdate.has(id));
      if (firstNewObjecttype) {
        return getSourceSelectOptionIdForObjecttype(firstNewObjecttype);
      }
    }

    // 2. Otherwise, try to select the persisted selected source (might be unset or outdated)
    const persistedSelection = cadenzaSessionStorage.getItem(
      getSourceSelectSessionStorageKey(),
    ) as SourceSelectOptionId | null;
    if (persistedSelection && selectData.objecttypesBySelectOptionId[persistedSelection]) {
      return persistedSelection;
    }

    // 3. Otherwise, take the datasource of the first attribute of the first data view of active view.
    const firstDataViewsAttributesObjecttype = getFirstDataViewsAttributesObjecttype();
    if (firstDataViewsAttributesObjecttype) {
      return getSourceSelectOptionIdForObjecttype(firstDataViewsAttributesObjecttype);
    }

    // 4. As fallback, just the first one from the datasource list
    return keys(selectData.objecttypesBySelectOptionId)[0];
  }

  get selectedSource() {
    // bug? the `undefined` is cast away, but it's definitely there when opening a workbook ...
    return this.#sourceSelect.selection[0]?.id as SourceSelectOptionId;
  }

  set selectedSource(selectedSource: SourceSelectOptionId) {
    this.#sourceSelect.selection = selectedSource;
    cadenzaSessionStorage.setItem(getSourceSelectSessionStorageKey(), selectedSource);
    const objecttypes = this.#selectData!.objecttypesBySelectOptionId[selectedSource]!;
    this.#dataBrowserItemList.updateList(objecttypes, isWorkbookVisible(getStore().getState()));
  }
}

customElements.define(COMPONENT_NAME, DataBrowserPanel);

function createSelectData(objecttypes: Objecttype[]) {
  const objecttypesByGroupCategory = Object.groupBy(objecttypes, getSelectGroupCategory);
  const selectData: SourceSelectData = { selectItems: [], objecttypesBySelectOptionId: {} };
  for (const category of SelectItemGroupCategoryOrder) {
    const categoryObjecttypes = objecttypesByGroupCategory[category];

    // Continue with next category, when we have no objecttypes for the current
    if (!categoryObjecttypes) {
      continue;
    }

    const categorySelectData = prepareSelectData(category, categoryObjecttypes);

    // Add separator line between categories
    const previousSelectItem = selectData.selectItems.at(-1);
    if (previousSelectItem) {
      previousSelectItem.addSeparationLineBelow = true;
    }

    // Merge select items
    selectData.selectItems.push(...categorySelectData.selectItems);

    // Merge dictionary containing Objecttypes by SelectOptionId
    for (const entry of entries(categorySelectData.objecttypesBySelectOptionId)) {
      const [sourceSelectOptionId, selectOptionObjecttypes] = entry!;
      selectData.objecttypesBySelectOptionId[sourceSelectOptionId] = selectOptionObjecttypes;
    }
  }
  return selectData;
}

function prepareSelectData(category: string, categoryObjecttypes: Objecttype[]): SourceSelectData {
  switch (category as SelectItemGroupCategory) {
    case 'WORKBOOK_STORAGE':
      return prepareWorkbookStorageSelectData(categoryObjecttypes);
    case 'COMPONENT_PROJECT':
      return prepareProjectSelectData(categoryObjecttypes);
    case 'DATASOURCE':
      return prepareDatasourceSelectData(categoryObjecttypes);
  }
}

function prepareWorkbookStorageSelectData(objecttypes: Objecttype[]): SourceSelectData {
  return {
    objecttypesBySelectOptionId: {
      '{WORKBOOK_STORAGE}': objecttypes,
    },
    selectItems: [
      {
        id: '{WORKBOOK_STORAGE}',
        text: i18n('search.workbookStorage.label'),
      },
    ],
  };
}

function prepareProjectSelectData(objecttypes: Objecttype[]): SourceSelectData {
  const objecttypesByProject = Object.groupBy(objecttypes, (ot) => itemIdToString(ot.projectId!));

  return {
    objecttypesBySelectOptionId: objecttypesByProject,
    selectItems: [
      {
        text: i18n('search.project.group.label'),
        children: entries(objecttypesByProject).map(([projectIdString, projectObjecttypes]) => {
          const projectId = projectObjecttypes![0].projectId!;
          return {
            id: projectIdString,
            projectId,
            text: getProjectById(getStore().getState(), projectId).printName,
            resourceType: 'project',
          };
        }),
      },
    ],
  };
}

function prepareDatasourceSelectData(objecttypes: Objecttype[]): SourceSelectData {
  const objecttypesGroupedRepositoryItemId = Object.groupBy(objecttypes, (ot) =>
    itemIdToString(DataSourceId.of(ot.dataSourceRepositoryName, ot.dataSourceName)),
  );

  const dataSourcesGroupedByRepository = entries(objecttypesGroupedRepositoryItemId).reduce(
    (data, [repositoryItemId, objecttypeGroup]) => {
      // Skip datasource if empty (e.g. all OTs in Workbook storage entry)
      const firstObjecttype = objecttypeGroup?.[0];
      if (firstObjecttype) {
        const dataSource = getDataSourceByObjecttypeId(getStore().getState(), firstObjecttype.id);
        let group = data.get(dataSource.itemId.repositoryName);
        if (!group) {
          group = [];
          data.set(dataSource.itemId.repositoryName, group);
        }
        group.push({
          resourceType: 'datasource',
          id: repositoryItemId, // The id used to determine the corresponding objecttypes
          text: dataSource.printName,
          type: dataSource.type,
          flavor: dataSource.flavor,
          repositoryPrintName: dataSource.repositoryPrintName,
        });
      }

      return data;
    },
    new Map<RepositoryName, DatasourceSelectOption[]>(),
  );

  return {
    objecttypesBySelectOptionId: objecttypesGroupedRepositoryItemId,
    selectItems: [...dataSourcesGroupedByRepository.keys()].map((repository) => {
      const children = dataSourcesGroupedByRepository.get(repository)!;
      const { repositoryPrintName, type } = children[0];
      return {
        id: repository,
        text: repositoryPrintName!,
        resourceType: 'datasource',
        type,
        repositoryPrintName,
        children,
      };
    }),
  };
}

function renderDatasourceSelection(dataSource: DatasourceSelectItem) {
  return h(
    `.${COMPONENT_NAME}--selected-source.ellipsis`,
    createDataSourceTile(dataSource.text, dataSource.type, dataSource.flavor),
  );
}

function renderWorkbookStorageSelection(selectOption: WorkbookStorageSelectOption) {
  return renderWorkbookStorageTile(selectOption, `${COMPONENT_NAME}--selected-workbook-storage`);
}

function templateWorkbookStorageResult(selectOption: WorkbookStorageSelectOption) {
  return renderWorkbookStorageTile(selectOption, `${COMPONENT_NAME}--option-workbook-storage`);
}

function renderWorkbookStorageTile(selectOption: WorkbookStorageSelectOption, styleClass: string) {
  return h(`.${styleClass}.ellipsis`, [
    createResourceLabel('workbook', selectOption.text),
    h('.d-stack-h.space-1', i18n('search.workbookStorage.description')),
  ]);
}

function renderProjectSelection(selectOption: ProjectSelectOption) {
  return h(`.${COMPONENT_NAME}--selected-project.ellipsis`, [
    createResourceLabel('project', selectOption.text),
    h('.d-stack-h.space-1', i18n('search.project.description')),
  ]);
}

function renderProjectResult(selectOption: ProjectSelectOption) {
  return h(`.${COMPONENT_NAME}--option-project.ellipsis`, [createResourceLabel('project', selectOption.text)]);
}

function templateGroupProjectResult(item: ProjectSelectionGroup) {
  return h<HTMLElement>('.repository-result.ellipsis', item.text);
}

/**
 * Returns the datasource of the first attribute of the first data view of the first view of the current worksheet.
 *
 * @return The name of the datasource if found.
 */
function getFirstDataViewsAttributesObjecttype() {
  const activeView = getActiveView(getStore().getState());

  if (!activeView?.dataViews.length) {
    return undefined;
  }

  const firstDataView = getDataViewById(getStore().getState(), activeView.dataViews[0]);
  const attributes = Object.values(firstDataView.attributes).flat();

  if (!attributes.length) {
    return undefined;
  }

  const firstAttributeId = getBaseAttributeId(attributes[0]);

  return getObjecttypeByAttributeId(getStore().getState(), firstAttributeId);
}

function isWorkbookStorageSelectOption(
  selectOption: SelectOption | SelectItem,
): selectOption is WorkbookStorageSelectOption {
  return (selectOption as WorkbookStorageSelectOption)?.id === '{WORKBOOK_STORAGE}';
}

function isProjectSelectOption(selectOption: SelectOption): selectOption is ProjectSelectOption {
  return (selectOption as ProjectSelectOption).projectId !== undefined;
}

function getPanelData(state: RootState) {
  return {
    objecttypes: getVisibleObjecttypes(state),
    dataSources: getVisibleDataSources(state),

    // to force reload when attribute settings are changed
    attributes: getAttributes(state),
    projects: getProject(getStore().getState())?.componentProjectIds?.map((projectId) =>
      getProjectById(getStore().getState(), projectId),
    ),
    canAddData: isWorkbookVisible(state),
    objecttypeDataDependencies: getObjecttypeDataDependencies(state),
  };
}

function getSelectGroupCategory(objecttype: Objecttype): SelectItemGroupCategory {
  if (isObjecttypeBoundToProject(objecttype)) {
    return isComponentProjectObjecttype(objecttype) ? 'COMPONENT_PROJECT' : 'DATASOURCE';
  }

  if (objecttype.boundState !== 'UNBOUND') {
    return 'WORKBOOK_STORAGE';
  }

  return 'DATASOURCE';
}

function renderSelection(selection: SelectOption) {
  if (isWorkbookStorageSelectOption(selection)) {
    return renderWorkbookStorageSelection(selection);
  } else if (isProjectSelectOption(selection)) {
    return renderProjectSelection(selection);
  } else {
    return renderDatasourceSelection(selection as DatasourceSelectOption);
  }
}

function renderResult(selection: SelectOption, searchText: string) {
  if (isWorkbookStorageSelectOption(selection)) {
    return templateWorkbookStorageResult(selection);
  } else if (isProjectSelectOption(selection)) {
    return renderProjectResult(selection);
  } else {
    return templateDatasourceResult(selection as DatasourceSelectOption, searchText);
  }
}

function getSourceSelectOptionIdForObjecttype(objecttype: Objecttype) {
  // Will create SourceSelectItem unnecessarily, but still it is easier than traversing the SelectData
  // or preparing a lookup table for all items
  return keys(createSelectData([objecttype]).objecttypesBySelectOptionId)[0];
}

function getSourceSelectSessionStorageKey() {
  const workbookName = getWorkbookNameAndHash(getStore().getState()).id;
  return `${SELECTED_SOURCE_STORAGE_KEY}${workbookName}`;
}

function getFilteredObjecttypes(objecttypes: Objecttype[]) {
  const objecttypeIdsToFilterOut = new Set(
    objecttypes
      .flatMap((objecttype) => objecttype?.enrichments ?? [])
      .filter(({ objecttypeId }) => !!getObjecttypeById(getStore().getState(), objecttypeId).boundState)
      .map((enrichmentObjecttype) => enrichmentObjecttype.objecttypeId),
  );

  return objecttypes.filter((objecttype) => !objecttypeIdsToFilterOut.has(objecttype.id));
}
